import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Grid,
    Typography,
    Box,
    Alert,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from "react-toastify";
import { obtenerGastosPorMedico, eliminarGastoMedico } from "../../services/gastoMedicoService";
import { getTramites } from "../../services/tramiteService";
import ModalAgregarGastoMedico from "../GastosMedicosComponentes/ModalAgregarGastoMedico";
import ModalEditarGastoMedico from "../GastosMedicosComponentes/ModalEditarGastoMedico";
import { getClientes } from '../../services/clienteService';
import 'jspdf-autotable';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

// Componentes estilizados
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '12px',
    boxShadow: 'var(--shadow-md)',
    border: 'none',
    marginBottom: theme.spacing(3),
}));

const ModalDetalleMedico = ({
                                open,
                                onClose,
                                medicoSeleccionado,
                                onEditarClick
                            }) => {
    const [gastosMedicos, setGastosMedicos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [tramites, setTramites] = useState([]);
    const [loadingGastos, setLoadingGastos] = useState(false);
    const [showAgregarGasto, setShowAgregarGasto] = useState(false);
    const [showEditarGasto, setShowEditarGasto] = useState(false);
    const [gastoSeleccionado, setGastoSeleccionado] = useState(null);
    const [dialogoEliminar, setDialogoEliminar] = useState({open: false, idGastomedico: null});

    console.log("ModalDetalleMedico - open:", open);
    console.log("ModalDetalleMedico - medicoSeleccionado:", medicoSeleccionado);

    // Cargar gastos médicos del médico
    const cargarGastosMedicos = async () => {
        if (!medicoSeleccionado?.idMedico) return;

        setLoadingGastos(true);
        try {
            const gastos = await obtenerGastosPorMedico(medicoSeleccionado.idMedico);
            console.log("Gastos cargados:", gastos);
            setGastosMedicos(gastos);
        } catch (error) {
            console.error("Error al cargar gastos médicos:", error);
            toast.error("No se pudieron cargar los gastos médicos");
        } finally {
            setLoadingGastos(false);
        }
    };

    // Formatear fecha
    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Calcular total de gastos
    const calcularTotalGastos = () => {
        return gastosMedicos.reduce((total, gasto) => total + parseFloat(gasto.monto || 0), 0);
    };

    // Manejar cuando se agrega un nuevo gasto
    const handleGastoAgregado = () => {
        cargarGastosMedicos();
        setShowAgregarGasto(false);
        toast.success("Gasto médico agregado exitosamente.");
    };

    // Manejar apertura de modal de edición
    const handleAbrirEditar = (gasto) => {
        setGastoSeleccionado(gasto.idGastomedico);
        setShowEditarGasto(true);
    };

    // Manejar cierre de modal de edición
    const handleCerrarEditar = () => {
        setShowEditarGasto(false);
        setGastoSeleccionado(null);
    };

        // Manejar actualización de gasto
        const handleGastoActualizado = () => {
            cargarGastosMedicos();
            toast.success("Gasto médico actualizado exitosamente.");
        };

        // Manejar apertura de diálogo de eliminación
        const abrirDialogoEliminar = (idGastomedico) => {
            setDialogoEliminar({open: true, idGastomedico});
        };

        // Manejar cierre de diálogo de eliminación
        const cerrarDialogoEliminar = () => {
            setDialogoEliminar({open: false, idGastomedico: null});
        };

        // Confirmar eliminación de gasto
        const confirmarEliminacion = async () => {
            if (!dialogoEliminar.idGastomedico) return;

            try {
                await eliminarGastoMedico(dialogoEliminar.idGastomedico);
                toast.success("Gasto médico eliminado exitosamente.");
                // Actualizar estado local para reflejar la eliminación inmediatamente
                setGastosMedicos(prev => prev.filter(g => g.idGastomedico !== dialogoEliminar.idGastomedico));
                cerrarDialogoEliminar();
            } catch (error) {
                console.error("Error al eliminar gasto:", error);
                toast.error("Error al eliminar el gasto médico.");
            }
        };

        const obtenerNombreCliente = (idTramite) => {
            // 1. Encontrar el objeto trámite por ID
            const tramiteEncontrado = tramites.find(t => t.idTramite === idTramite);
            if (!tramiteEncontrado) return `Trámite #${idTramite}`; // Fallback si no hay trámite

            // Asumimos que idCliente en trámite es el CUIL del cliente
            const cuilCliente = tramiteEncontrado.idCliente;

            // 2. Encontrar el cliente por CUIL
            const cliente = clientes.find(c => c.cuil === cuilCliente);

            // 3. Devolver nombre o fallback
            return cliente ? `${cliente.nombre} ${cliente.apellido}` : `CUIL: ${cuilCliente}`;
        };

        const cargarDatosRelacionados = async () => {
            try {
                // Carga simultánea de datos
                const [tramitesData, clientesData] = await Promise.all([
                    getTramites(),
                    getClientes()
                ]);
                setTramites(tramitesData);
                setClientes(clientesData);
            } catch (error) {
                console.error("Error al cargar datos relacionados:", error);
            }
        };

        useEffect(() => {
            if (open && medicoSeleccionado) {
                console.log("Cargando datos para médico:", medicoSeleccionado.idMedico);
                cargarGastosMedicos();
                cargarDatosRelacionados();
            }
        }, [open, medicoSeleccionado]);

        // Si no hay médico seleccionado, no mostrar nada
        if (!medicoSeleccionado) {
            console.log("No hay médico seleccionado");
            return null;
        }

        return (
            <>
                <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            minHeight: '600px'
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            py: 3
                        }}
                    >
                        Detalles del Médico
                    </DialogTitle>

                    <DialogContent sx={{mt: 2, pb: 1}}>
                        {/* Información del Médico */}
                        <StyledCard>
                            <Box sx={{p: 3}}>
                                <Grid container spacing={20}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h5" color="primary" gutterBottom>
                                            Información Profesional

                                        </Typography>
                                        <Box sx={{mb: 2}}>
                                            <Typography variant="h6" gutterBottom>
                                                <strong>Nombre:</strong> {medicoSeleccionado.nombre} {medicoSeleccionado.apellido}
                                            </Typography>
                                            <Typography variant="h6" gutterBottom>
                                                <strong>Matrícula:</strong> {medicoSeleccionado.matricula || "—"}
                                            </Typography>
                                            <Typography variant="h6" gutterBottom>
                                                <strong>Estado:</strong>
                                                <Chip
                                                    label={medicoSeleccionado.activo ? "Activo" : "Inactivo"}
                                                    color={medicoSeleccionado.activo ? "success" : "default"}
                                                    size="medium"
                                                    sx={{ml: 1, fontSize: '14px'}}
                                                />
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" color="primary" gutterBottom>
                                            &nbsp;
                                        </Typography>
                                        <Box sx={{mb: 2}}>

                                            <Typography variant="h6" gutterBottom>
                                                <strong>Especialidad:</strong> {medicoSeleccionado.especialidad || "—"}
                                            </Typography>
                                            <Typography variant="h6" gutterBottom>
                                                <strong>Teléfono:</strong> {medicoSeleccionado.telefono || "—"}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                                    <button
                                        className="btn btn-outline-primary rounded-pill px-4 d-flex align-items-center gap-2 btn-lg"
                                        onClick={() => onEditarClick(medicoSeleccionado)}
                                    >
                                        Editar información
                                    </button>
                                </Box>
                            </Box>
                        </StyledCard>

                        {/* Gastos Médicos */}
                        <StyledCard>
                            <Box sx={{p: 3}}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <Typography variant="h5" color="primary.main">

                                        Gastos Médicos Asociados
                                    </Typography>
                                    <button
                                        className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2 btn-lg"
                                        onClick={() => setShowAgregarGasto(true)}
                                    >
                                        Agregar Gasto
                                    </button>
                                </Box>

                                {loadingGastos ? (
                                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4}}>
                                        <CircularProgress/>
                                        <Typography variant="body1" sx={{ml: 2}}>
                                            Cargando gastos médicos...
                                        </Typography>
                                    </Box>
                                ) : gastosMedicos.length === 0 ? (
                                    <Alert severity="info" sx={{mb: 2}}>
                                        No hay gastos médicos registrados para este médico.
                                    </Alert>
                                ) : (
                                    <>
                                        <Box sx={{
                                            mb: 3,
                                            p: 2,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 2,
                                            backgroundColor: '#f9f9f9'
                                        }}>
                                            <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                                                <span>Total Gastado: </span>
                                                <span>
                                            ${calcularTotalGastos().toLocaleString('es-ES', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                        </span>

                                                <span style={{margin: '0 50px'}}>|</span>

                                                <span>Total Gastos: </span>
                                                <span>
                                            {gastosMedicos.length}
                                        </span>

                                                <span style={{margin: '0 50px'}}>|</span>

                                                <span>Promedio por Gasto: </span>
                                                <span>
                                            ${(calcularTotalGastos() / gastosMedicos.length).toLocaleString('es-ES', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                        </span>
                                            </Typography>
                                        </Box>

                                        {/* Tabla de Gastos */}
                                        <TableContainer component={Paper} sx={{
                                            borderRadius: 2,
                                            boxShadow: 'none',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        {/* Encabezado: Fecha - Centrado y h6 */}
                                                        <TableCell align="center" sx={{
                                                            backgroundColor: 'primary.main',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '1.1rem'
                                                        }}>
                                                            Fecha
                                                        </TableCell>
                                                        {/* Encabezado: Trámite ID - Centrado y h6 */}
                                                        <TableCell align="center" sx={{
                                                            backgroundColor: 'primary.main',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '1.1rem'
                                                        }}>
                                                            Nombre Cliente
                                                        </TableCell>
                                                        {/* Encabezado: Monto - Centrado y h6 */}
                                                        <TableCell align="center" sx={{
                                                            backgroundColor: 'primary.main',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '1.1rem'
                                                        }}>
                                                            Monto
                                                        </TableCell>
                                                        {/* Encabezado: Comentario - Centrado y h6 */}
                                                        <TableCell align="center" sx={{
                                                            backgroundColor: 'primary.main',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '1.1rem'
                                                        }}>
                                                            Comentario
                                                        </TableCell>
                                                        {/* Encabezado: Acciones - Centrado y h6 */}
                                                        <TableCell align="center" sx={{
                                                            backgroundColor: 'primary.main',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '1.1rem'
                                                        }}>
                                                            Acciones
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {gastosMedicos.map((gasto, idx) => (
                                                        <TableRow key={gasto.idGastomedico || idx} hover>
                                                            {/* Celda: Fecha - Centrada y tamaño body1 */}
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label={formatearFecha(gasto.fecha)}
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    size="medium" // Tamaño medio para armonizar
                                                                    sx={{fontSize: '14px'}}
                                                                />
                                                            </TableCell>
                                                            {/* Celda: Trámite ID - Centrada y tamaño body1 */}
                                                            <TableCell align="center">
                                                                <Typography variant="body1" fontWeight="medium">
                                                                    {obtenerNombreCliente(gasto.idTramite)}
                                                                </Typography>
                                                            </TableCell>
                                                            {/* Celda: Monto - Centrada y tamaño body1 */}
                                                            <TableCell align="center">
                                                                <Typography variant="body1" fontWeight="bold"
                                                                            color="success.main">
                                                                    ${parseFloat(gasto.monto).toLocaleString('es-ES', {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                })}
                                                                </Typography>
                                                            </TableCell>
                                                            {/* Celda: Comentario - Centrada, tamaño body1, sin límite de ancho */}
                                                            <TableCell align="center">
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        // Eliminamos el maxWidth y el textOverflow para ver más comentario
                                                                        whiteSpace: 'normal',
                                                                        fontWeight: 'regular'
                                                                    }}
                                                                    title={gasto.comentario}
                                                                >
                                                                    {gasto.comentario || "—"}
                                                                </Typography>
                                                            </TableCell>
                                                            {/* Celda: Acciones - Centrada */}
                                                            <TableCell align="center">
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    gap: 1,
                                                                    justifyContent: 'center'
                                                                }}>
                                                                    <button
                                                                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                                                        onClick={() => handleAbrirEditar(gasto)}
                                                                    >
                                                                        <EditIcon fontSize="small"/>
                                                                        Editar
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                                                                        onClick={() => abrirDialogoEliminar(gasto.idGastomedico)}
                                                                    >
                                                                        <DeleteIcon fontSize="small"/>
                                                                        Eliminar
                                                                    </button>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}
                            </Box>
                        </StyledCard>
                    </DialogContent>

                    <DialogActions sx={{p: 3, gap: 1}}>
                        <button
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={onClose}
                        >
                            Cerrar
                        </button>
                    </DialogActions>
                </Dialog>


                {/* Modal para agregar gasto médico */}
                <ModalAgregarGastoMedico
                    open={showAgregarGasto}
                    onClose={() => setShowAgregarGasto(false)}
                    preSelectedMedico={medicoSeleccionado?.idMedico}
                    onGastoAgregado={handleGastoAgregado}
                    tramites={tramites}
                />

                {/* Modal para editar gasto médico */}
                <ModalEditarGastoMedico
                    open={showEditarGasto}
                    onClose={handleCerrarEditar}
                    gastoId={gastoSeleccionado}
                    onGastoActualizado={handleGastoActualizado}
                />

                {/* Diálogo de confirmación de eliminación */}
                <Dialog
                    open={dialogoEliminar.open}
                    onClose={cerrarDialogoEliminar}
                >
                    <DialogTitle sx={{fontWeight: 'bold', color: 'error.main'}}>
                        Confirmar Eliminación
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            ¿Estás seguro de eliminar este gasto médico? Esta acción no se puede deshacer.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <button className="btn btn-outline-secondary" onClick={cerrarDialogoEliminar}>
                            Cancelar
                        </button>
                        <button className="btn btn-danger" onClick={confirmarEliminacion} autoFocus>
                            Eliminar
                        </button>
                    </DialogActions>
                </Dialog>
            </>
        );
    };

export default ModalDetalleMedico;
